/**
 * DashboardKpis — 4-card KPI strip
 * canonical: lines 608–629
 *
 * Card 1: Total pipeline  — SUM(original_contract) computed client-side
 * Card 2: Billed          — /api/reports/stats → data.total_billed
 * Card 3: Outstanding     — /api/reports/stats → data.outstanding
 * Card 4: Collected · MTD — /api/reports/stats → data.collected_mtd (+ Stripe caption)
 */

import { useQuery }       from '@tanstack/react-query'
import { useNavigate }    from 'react-router-dom'
import { fetchDashboardStats, fetchProjects } from '../../api/dashboard'
import { formatCurrency } from '../../lib/formatters'
import type { DrillDownView } from './DashboardDrillDownPanel'

// ── KpiCard atom ─────────────────────────────────────────────────────────

interface KpiCardProps {
  label:       string
  value:       string
  delta?:      string
  deltaClass?: string       // 'pos' | 'neg' | undefined
  onClick?:    () => void
  skeleton?:   boolean
  'data-testid'?: string
}

function KpiCard({ label, value, delta, deltaClass, onClick, skeleton, ...rest }: KpiCardProps) {
  return (
    /* canonical: lines 609–628 — .kpi */
    <div
      className="kpi"
      onClick={onClick}
      style={onClick ? { cursor: 'pointer' } : undefined}
      data-testid={rest['data-testid']}
    >
      <div className="kpi-l">{label}</div>
      <div className="kpi-v">
        {skeleton
          ? <span style={{ opacity: 0.35 }}>—</span>
          : value}
      </div>
      {delta && !skeleton && (
        <div className={`kpi-d${deltaClass ? ' ' + deltaClass : ''}`}>{delta}</div>
      )}
    </div>
  )
}

// ── DashboardKpis ─────────────────────────────────────────────────────────

interface DashboardKpisProps {
  onDrillDown?: (view: DrillDownView) => void
}

export function DashboardKpis({ onDrillDown }: DashboardKpisProps) {
  const navigate = useNavigate()

  /* /api/reports/stats — billed + outstanding aggregates */
  const { data: stats, isPending: statsLoading } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn:  fetchDashboardStats,
  })

  /* /api/projects — for pipeline (SUM original_contract) */
  const { data: projects, isPending: projLoading } = useQuery({
    queryKey: ['projects'],
    queryFn:  fetchProjects,
  })

  const skeleton = statsLoading || projLoading

  const pipeline = projects
    ? projects.reduce((s, p) => s + Number(p.original_contract ?? 0), 0)
    : 0

  return (
    /* canonical: line 608 — .kpis */
    <div className="kpis">

      {/* canonical: lines 609–613 — Total pipeline (clickable → /projects) */}
      <KpiCard
        label="Total pipeline"
        value={formatCurrency(pipeline)}
        delta={skeleton ? undefined : 'contract value · all projects'}
        deltaClass="pos"
        onClick={() => navigate('/projects')}
        skeleton={skeleton}
        data-testid="kpi-pipeline"
      />

      {/* canonical: lines 614–618 — Billed (drill-down) */}
      <KpiCard
        label="Billed"
        value={formatCurrency(stats?.total_billed ?? 0)}
        delta={skeleton ? undefined : `${stats?.payapps ?? 0} pay apps · ${stats?.projects ?? 0} projects`}
        onClick={() => onDrillDown?.('billed')}
        skeleton={skeleton}
        data-testid="kpi-billed"
      />

      {/* canonical: lines 619–623 — Outstanding (drill-down → collect screen) */}
      <KpiCard
        label="Outstanding"
        value={formatCurrency(stats?.outstanding ?? 0)}
        delta={skeleton ? undefined : 'awaiting payment'}
        deltaClass="neg"
        onClick={() => onDrillDown?.('outstanding')}
        skeleton={skeleton}
        data-testid="kpi-outstanding"
      />

      {/* canonical: lines 624–628 — Collected this month */}
      <KpiCard
        label="Collected · this month"
        value={formatCurrency(stats?.collected_mtd ?? 0)}
        delta={skeleton ? undefined : 'recorded this month'}
        deltaClass="pos"
        onClick={() => onDrillDown?.('collected')}
        skeleton={skeleton}
        data-testid="kpi-collected-mtd"
      />

    </div>
  )
}
